import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Grid, 
  Typography, 
  Box, 
  Card, 
  CardContent, 
  Button, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Chip, 
  Alert, 
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Divider,
  Stack,
  IconButton,
  Fade,
  Grow,
  Paper
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  AccessTime as AccessTimeIcon,
  CalendarToday as CalendarTodayIcon,
  CreditCard as CreditCardIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  AccountCircle as AccountCircleIcon,
  Star as StarIcon,
  Diamond as DiamondIcon,
  TrendingUp as TrendingUpIcon,
  Shield as ShieldIcon,
  Edit as EditIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import authService from '../services/authService';
import pricingService from '../services/pricingService';
import stripeService from '../services/stripeService';

const SubscriptionPage = () => {
  const [user, setUser] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [annualPricingData, setAnnualPricingData] = useState({});
  const [loadingAnnualPricing, setLoadingAnnualPricing] = useState(false);
  const [processingAction, setProcessingAction] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [refreshingUser, setRefreshingUser] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // Check if subscription is cancelled but still active (will end at period end)
  const isSubscriptionCancelled = user?.payment_status === 'cancelled_at_period_end';

  // Function to refresh user data
  const refreshUserData = async () => {
    try {
      setRefreshingUser(true);
      const freshUser = await authService.refreshUserData();
      setUser(freshUser);
      return freshUser;
    } catch (error) {
      console.error('Failed to refresh user data:', error);
    } finally {
      setRefreshingUser(false);
    }
  };
  
  // Check for Stripe success/cancel parameters
  useEffect(() => {
    const success = searchParams.get('success');
    const canceled = searchParams.get('canceled');
    const sessionId = searchParams.get('session_id');
    
    if (success === 'true') {
      setSuccessMessage('🎉 Payment successful! Your subscription has been activated.');
      
      // Handle successful payment
      if (sessionId) {
        stripeService.handlePaymentSuccess(sessionId);
      }
      
      // Refresh user data immediately to show updated subscription
      refreshUserData().then((freshUser) => {
        if (freshUser) {
          console.log('User data refreshed after payment:', freshUser);
        }
      });
      
    } else if (canceled === 'true') {
      setError('Payment was canceled. You can try again anytime.');
    }
  }, [searchParams]);
  
  // Fetch user data and pricing plans on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Check if user is logged in
        let currentUser = authService.getCurrentUser();
        if (!currentUser) {
          navigate('/login');
          return;
        }
        
        // Always fetch fresh user data from server
        try {
          currentUser = await authService.refreshUserData();
        } catch (error) {
          console.error('Failed to refresh user data on mount:', error);
          // Use cached user data if refresh fails
        }
        
        setUser(currentUser);
        
        // Fetch pricing plans
        const plansData = await pricingService.getPricingPlans();
        setPlans(plansData);
        
        // Check for incoming state from pricing card selection
        if (location.state?.selectedPlan && location.state?.billingCycle) {
          setBillingCycle(location.state.billingCycle);
        } else if (currentUser.billing_cycle) {
          // Otherwise, set billing cycle from user's current subscription
          setBillingCycle(currentUser.billing_cycle);
        }
      } catch (error) {
        console.error('Error loading subscription data', error);
        setError('Failed to load subscription information. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [navigate, location.state]);
  
  // Fetch annual pricing data for paid plan
  useEffect(() => {
    const fetchAnnualPricing = async () => {
      try {
        const paidPlans = plans.filter(plan => plan.slug !== 'free');
        if (paidPlans.length === 0) return;
        
        setLoadingAnnualPricing(true);
        
        const pricingData = {};
        
        for (const plan of paidPlans) {
          try {
            const response = await pricingService.getAnnualPricing(plan.slug);
            pricingData[plan.slug] = response;
          } catch (error) {
            console.error(`Failed to get annual pricing for ${plan.slug}:`, error);
            pricingData[plan.slug] = getFallbackAnnualPricing(plan.slug);
          }
        }
        
        setAnnualPricingData(pricingData);
      } catch (error) {
        console.error('Error fetching annual pricing data:', error);
      } finally {
        setLoadingAnnualPricing(false);
      }
    };
    
    if (plans.length > 0) {
      fetchAnnualPricing();
    }
  }, [plans]);
  
  const getFallbackAnnualPricing = (planSlug) => {
    if (planSlug === 'standard') {
      return {
        price: '$299',
        period: 'per year',
        savings: 'Save $49 annually'
      };
    }
    return null;
  };
  
  const getAnnualPricing = (planSlug) => {
    if (!planSlug || planSlug === 'free') {
      return null;
    }
    
    return annualPricingData[planSlug] || getFallbackAnnualPricing(planSlug);
  };
  
  const handleUpgradeClick = async (plan, cycleOverride = null) => {
    const currentCycle = cycleOverride || billingCycle;
    
    if (plan.slug === 'free') {
      // Free plan isn't an upgrade, ignore
      return;
    }

    try {
      setProcessingAction('upgrade');
      setError('');
      
      // Use simple redirect to Stripe checkout
      await stripeService.redirectToCheckout(plan.slug, currentCycle);
      
    } catch (error) {
      console.error('Payment error:', error);
      setError(
        error.response?.data?.error || 
        'Failed to initiate payment. Please try again.'
      );
    } finally {
      setProcessingAction('');
    }
  };
  
  const handleBillingCycleChange = (event, newBillingCycle) => {
    if (newBillingCycle !== null) {
      setBillingCycle(newBillingCycle);
    }
  };
  
  const handleCancelClick = () => {
    setCancelDialogOpen(true);
  };
  
  const handleCancelConfirm = async () => {
    try {
      setProcessingAction('cancel');
      setError('');
      
      const result = await stripeService.cancelSubscription();
      
      // Update user data
      await refreshUserData();
      
      setSuccessMessage(
        `✅ Subscription cancelled successfully! You'll continue to have access until ${
          user?.subscription_expires_at 
            ? new Date(user.subscription_expires_at).toLocaleDateString()
            : 'the end of your billing period'
        }.`
      );
      
    } catch (error) {
      console.error('Cancel error:', error);
      setError(
        error.response?.data?.error || 
        'Failed to cancel subscription. Please try again later.'
      );
    } finally {
      setProcessingAction('');
      setCancelDialogOpen(false);
    }
  };
  
  const handleReactivate = async () => {
    try {
      setProcessingAction('reactivate');
      setError('');
      
      const result = await stripeService.reactivateSubscription();
      
      // Update user data
      await refreshUserData();
      
      setSuccessMessage('✅ Subscription reactivated successfully!');
      
    } catch (error) {
      console.error('Reactivate error:', error);
      setError(
        error.response?.data?.error || 
        'Failed to reactivate subscription. Please try again later.'
      );
    } finally {
      setProcessingAction('');
    }
  };

  // Get user's plan icon
  const getPlanIcon = (planSlug) => {
    switch (planSlug) {
      case 'standard':
        return <DiamondIcon sx={{ fontSize: 40, color: 'primary.main' }} />;
      case 'free':
      default:
        return <AccountCircleIcon sx={{ fontSize: 40, color: 'grey.500' }} />;
    }
  };

  // Get plan color theme
  const getPlanTheme = (planSlug) => {
    switch (planSlug) {
      case 'standard':
        return {
          gradient: 'linear-gradient(135deg, #5980a6 0%, #3f6288 100%)',
          color: 'primary.main',
          bgColor: 'rgba(102, 126, 234, 0.08)'
        };
      case 'free':
      default:
        return {
          gradient: 'linear-gradient(135deg, #5980a6 0%, #cf6f61 100%)',
          color: 'grey.600',
          bgColor: 'grey.50'
        };
    }
  };

  // Get plans header gradient (similar to plan theme but more subtle)
  const getPlansHeaderGradient = () => {
    return 'linear-gradient(135deg, #5980a6 0%, #3f6288 100%)';
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Skeleton variant="text" width={300} height={48} sx={{ mb: 2 }} />
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
          </Grid>
          <Grid item xs={12} md={8}>
            <Skeleton variant="rectangular" height={600} sx={{ borderRadius: 2 }} />
          </Grid>
        </Grid>
      </Container>
    );
  }

  if (!user) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">Unable to load user data. Please try refreshing the page.</Alert>
      </Container>
    );
  }

  const planTheme = getPlanTheme(user.subscription_plan);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header Section */}
      <Fade in timeout={800}>
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h3" fontWeight={700} sx={{ mb: 1 }}>
                My Subscription
              </Typography>
              <Typography variant="h6" color="text.secondary">
                Manage your plan and billing preferences
              </Typography>
            </Box>
            
            <IconButton
              onClick={refreshUserData}
              disabled={refreshingUser}
              sx={{ 
                bgcolor: 'background.paper',
                boxShadow: 1,
                '&:hover': { boxShadow: 2 }
              }}
            >
              {refreshingUser ? <CircularProgress size={24} /> : <RefreshIcon />}
            </IconButton>
          </Box>
          
          {successMessage && (
            <Grow in timeout={600}>
              <Alert 
                severity="success" 
                sx={{ mb: 3, borderRadius: 2 }}
                action={
                  <IconButton
                    size="small"
                    onClick={() => setSuccessMessage('')}
                  >
                    <CancelIcon fontSize="inherit" />
                  </IconButton>
                }
              >
                {successMessage}
              </Alert>
            </Grow>
          )}
          
          {error && (
            <Grow in timeout={600}>
              <Alert 
                severity="error" 
                sx={{ mb: 3, borderRadius: 2 }}
                action={
                  <IconButton
                    size="small"
                    onClick={() => setError('')}
                  >
                    <CancelIcon fontSize="inherit" />
                  </IconButton>
                }
              >
                {error}
              </Alert>
            </Grow>
          )}
        </Box>
      </Fade>

      <Grid container spacing={4}>
        {/* Left Column - Subscription Status Card */}
        <Grid item xs={12} md={4}>
          <Fade in timeout={1000}>
            <Card 
              sx={{ 
                borderRadius: 3,
                overflow: 'hidden',
                background: planTheme.gradient,
                color: 'white',
                position: 'relative',
                minHeight: 420
              }}
            >
              {/* Background pattern */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: '100%',
                  height: '100%',
                  opacity: 0.1,
                  background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Ccircle cx="7" cy="7" r="3"/%3E%3Ccircle cx="27" cy="7" r="3"/%3E%3Ccircle cx="47" cy="7" r="3"/%3E%3Ccircle cx="7" cy="27" r="3"/%3E%3Ccircle cx="27" cy="27" r="3"/%3E%3Ccircle cx="47" cy="27" r="3"/%3E%3Ccircle cx="7" cy="47" r="3"/%3E%3Ccircle cx="27" cy="47" r="3"/%3E%3Ccircle cx="47" cy="47" r="3"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
                }}
              />
              
              <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                {/* Plan Icon and Name */}
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      mx: 'auto',
                      mb: 2,
                      bgcolor: 'rgba(255, 255, 255, 0.2)',
                      backdropFilter: 'none',
                    }}
                  >
                    {getPlanIcon(user.subscription_plan)}
                  </Avatar>
                  
                  <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
                    {user.subscription_plan?.charAt(0)?.toUpperCase() + user.subscription_plan?.slice(1)} Plan
                  </Typography>
                  
                  {user.subscription_plan !== 'free' && user.billing_cycle && (
                    <Chip
                      label={`${user.billing_cycle.charAt(0).toUpperCase() + user.billing_cycle.slice(1)} billing`}
                      sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                        color: 'white',
                        fontWeight: 600
                      }}
                    />
                  )}
                </Box>

                {/* Status Indicators */}
                <Stack spacing={2} sx={{ mb: 3 }}>
                  {isSubscriptionCancelled && (
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      p: 2,
                      bgcolor: 'rgba(255, 152, 0, 0.25)',
                      borderRadius: 2,
                      border: '1px solid rgba(255, 152, 0, 0.4)'
                    }}>
                      <WarningIcon sx={{ mr: 1.5, color: '#ffb74d' }} />
                      <Box>
                        <Typography variant="body2" fontWeight={700} sx={{ color: '#fff' }}>
                          Cancellation Scheduled
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)' }}>
                          Access ends {user.subscription_expires_at 
                            ? new Date(user.subscription_expires_at).toLocaleDateString() 
                            : 'at period end'}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  
                  {user.subscription_plan !== 'free' && user.subscription_time_remaining > 0 && (
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      p: 2,
                      bgcolor: 'rgba(255, 255, 255, 0.15)',
                      borderRadius: 2
                    }}>
                      <AccessTimeIcon sx={{ mr: 1 }} />
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {user.subscription_time_remaining} days remaining
                        </Typography>
                        <Typography variant="caption">
                          {isSubscriptionCancelled ? 'Until access ends' : 'Until next billing cycle'}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Stack>

                {/* Key Dates */}
                <Stack spacing={1.5} sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Started:</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {user.subscription_started_at ? new Date(user.subscription_started_at).toLocaleDateString() : 'N/A'}
                    </Typography>
                  </Box>
                  
                  {user.subscription_plan !== 'free' && user.subscription_expires_at && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        {isSubscriptionCancelled ? 'Ends:' : 'Renews:'}
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {new Date(user.subscription_expires_at).toLocaleDateString()}
                      </Typography>
                    </Box>
                  )}
                  
                  {user.payment_method && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>Payment:</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {user.payment_method === 'stripe' ? 'Credit Card' : user.payment_method}
                      </Typography>
                    </Box>
                  )}
                </Stack>

                {/* Action Button */}
                <Box sx={{ mt: 'auto' }}>
                  {user.subscription_plan === 'free' ? (
                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      onClick={() => {
                        const standardPlan = plans.find(p => p.slug === 'standard');
                        if (standardPlan) handleUpgradeClick(standardPlan);
                      }}
                      disabled={processingAction === 'upgrade'}
                      startIcon={processingAction === 'upgrade' ? <CircularProgress size={20} /> : <TrendingUpIcon />}
                      sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.9)',
                        color: 'primary.main',
                        fontWeight: 700,
                        py: 1.5,
                        borderRadius: 2,
                        '&:hover': {
                          bgcolor: 'white',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {processingAction === 'upgrade' ? 'Processing...' : 'Upgrade to Premium'}
                    </Button>
                  ) : isSubscriptionCancelled ? (
                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      onClick={handleReactivate}
                      disabled={processingAction === 'reactivate'}
                      startIcon={processingAction === 'reactivate' ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                      sx={{
                        bgcolor: 'rgba(76, 175, 80, 0.9)',
                        color: 'white',
                        fontWeight: 700,
                        py: 1.5,
                        borderRadius: 2,
                        '&:hover': {
                          bgcolor: 'success.main',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {processingAction === 'reactivate' ? 'Reactivating...' : 'Reactivate Subscription'}
                    </Button>
                  ) : (
                    <Button
                      fullWidth
                      variant="outlined"
                      size="large"
                      onClick={handleCancelClick}
                      disabled={processingAction === 'cancel'}
                      startIcon={processingAction === 'cancel' ? <CircularProgress size={20} /> : <CancelIcon />}
                      sx={{
                        borderColor: 'rgba(255, 255, 255, 0.5)',
                        color: 'white',
                        fontWeight: 600,
                        py: 1.5,
                        borderRadius: 2,
                        '&:hover': {
                          borderColor: 'rgba(255, 100, 100, 0.8)',
                          bgcolor: 'rgba(255, 100, 100, 0.15)',
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {processingAction === 'cancel' ? 'Processing...' : 'Cancel Subscription'}
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Fade>
        </Grid>

        {/* Right Column - Available Plans */}
        <Grid item xs={12} md={8}>
          <Fade in timeout={1200}>
            <Card sx={{ borderRadius: 3, overflow: 'hidden', minHeight: 420 }}>
              {/* Header with gradient background */}
              <Box sx={{ 
                p: 3,
                background: getPlansHeaderGradient(),
                color: 'white',
                position: 'relative',
                overflow: 'hidden'
              }}>
                {/* Background pattern for header */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: '100%',
                    height: '100%',
                    opacity: 0.1,
                    background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.15"%3E%3Ccircle cx="7" cy="7" r="2"/%3E%3Ccircle cx="27" cy="7" r="2"/%3E%3Ccircle cx="47" cy="7" r="2"/%3E%3Ccircle cx="7" cy="27" r="2"/%3E%3Ccircle cx="27" cy="27" r="2"/%3E%3Ccircle cx="47" cy="27" r="2"/%3E%3Ccircle cx="7" cy="47" r="2"/%3E%3Ccircle cx="27" cy="47" r="2"/%3E%3Ccircle cx="47" cy="47" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
                  }}
                />
                
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                  <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
                    Available Plans
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9, mb: 3 }}>
                    Choose the plan that best fits your trading needs
                  </Typography>
                  
                  {/* Billing cycle selector */}
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2
                  }}>
                    <Typography variant="body2" fontWeight={600} sx={{ opacity: 0.9 }}>
                      Billing:
                    </Typography>
                    <ToggleButtonGroup
                      value={billingCycle}
                      exclusive
                      onChange={handleBillingCycleChange}
                      size="small"
                      sx={{ 
                        '& .MuiToggleButton-root': {
                          borderRadius: '8px',
                          border: '1px solid rgba(255, 255, 255, 0.3)',
                          color: 'rgba(255, 255, 255, 0.8)',
                          '&.Mui-selected': {
                            bgcolor: 'rgba(255, 255, 255, 0.2)',
                            color: 'white',
                            border: '1px solid rgba(255, 255, 255, 0.5)',
                            '&:hover': {
                              bgcolor: 'rgba(255, 255, 255, 0.3)',
                            }
                          },
                          '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.1)',
                          }
                        }
                      }}
                    >
                      <ToggleButton value="monthly">
                        Monthly
                      </ToggleButton>
                      <ToggleButton value="annual">
                        Annual
                      </ToggleButton>
                    </ToggleButtonGroup>
                    {billingCycle === 'annual' && (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Chip
                          icon={<StarIcon />}
                          label="Save up to 17%"
                          sx={{
                            bgcolor: 'rgba(76, 175, 80, 0.2)',
                            color: 'white',
                            border: '1px solid rgba(76, 175, 80, 0.3)',
                            '& .MuiChip-icon': {
                              color: 'white'
                            }
                          }}
                          size="small"
                          variant="outlined"
                        />
                        {loadingAnnualPricing && <CircularProgress size={16} sx={{ ml: 1, color: 'white' }} />}
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
              
              {/* Plans Grid */}
              <CardContent sx={{ p: 3 }}>
                <Grid container spacing={3}>
                  {plans.map((plan, index) => {
                    // Determine price and period based on current billing cycle
                    const priceDetails = billingCycle === 'annual' && plan.slug !== 'free'
                      ? getAnnualPricing(plan.slug)
                      : { price: plan.price, period: plan.period };
                    
                    const isCurrentPlan = plan.slug === user?.subscription_plan;
                    
                    return (
                      <Grid item xs={12} sm={6} key={plan.id}>
                        <Grow in timeout={800 + (index * 200)}>
                          <Paper
                            elevation={isCurrentPlan ? 8 : 2}
                            sx={{ 
                              p: 3,
                              borderRadius: 2,
                              height: '100%',
                              border: isCurrentPlan ? '2px solid' : '1px solid',
                              borderColor: isCurrentPlan ? 'primary.main' : 'divider',
                              bgcolor: isCurrentPlan ? 'primary.50' : 'background.paper',
                              position: 'relative',
                              overflow: 'hidden',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: 4
                              }
                            }}
                          >
                            {isCurrentPlan && (
                              <Chip
                                label="Current Plan"
                                color="primary"
                                size="small"
                                sx={{
                                  position: 'absolute',
                                  top: 12,
                                  right: 12,
                                  fontWeight: 600
                                }}
                              />
                            )}
                            
                            {/* Plan Header */}
                            <Box sx={{ mb: 3 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                {plan.slug === 'standard' ? (
                                  <DiamondIcon sx={{ mr: 1, color: 'primary.main' }} />
                                ) : (
                                  <ShieldIcon sx={{ mr: 1, color: 'grey.500' }} />
                                )}
                                <Typography variant="h6" fontWeight={700}>
                                  {plan.name}
                                </Typography>
                              </Box>
                              
                              <Box sx={{ mb: 2 }}>
                                {loadingAnnualPricing && billingCycle === 'annual' && plan.slug !== 'free' ? (
                                  <Skeleton width={100} height={40} />
                                ) : (
                                  <>
                                    <Typography variant="h4" fontWeight={700} color="primary.main">
                                      {priceDetails?.price}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      {priceDetails?.period}
                                    </Typography>
                                  </>
                                )}
                              </Box>
                              
                              {billingCycle === 'annual' && plan.slug !== 'free' && priceDetails?.savings && (
                                <Chip
                                  label={priceDetails.savings}
                                  color="success"
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                            </Box>
                            
                            {/* Features List */}
                            <List dense sx={{ mb: 3 }}>
                              {plan.features.map((feature, index) => (
                                <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                                  <ListItemIcon sx={{ minWidth: 32 }}>
                                    {feature.included ? (
                                      <CheckCircleIcon color="success" fontSize="small" />
                                    ) : (
                                      <CancelIcon color="disabled" fontSize="small" />
                                    )}
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={feature.name}
                                    primaryTypographyProps={{
                                      variant: 'body2',
                                      sx: { 
                                        textDecoration: feature.included ? 'none' : 'line-through',
                                        color: feature.included ? 'text.primary' : 'text.disabled',
                                        fontWeight: feature.included ? 500 : 400
                                      }
                                    }}
                                  />
                                </ListItem>
                              ))}
                            </List>
                            
                            {/* Action Button */}
                            <Box sx={{ mt: 'auto' }}>
                              {isCurrentPlan ? (
                                <Button
                                  fullWidth
                                  variant="contained"
                                  color="success"
                                  disabled
                                  sx={{ 
                                    py: 1.5,
                                    borderRadius: 2,
                                    fontWeight: 600
                                  }}
                                >
                                  Current Plan
                                </Button>
                              ) : plan.slug === 'free' ? (
                                <Button
                                  fullWidth
                                  variant="outlined"
                                  disabled
                                  sx={{ 
                                    py: 1.5,
                                    borderRadius: 2,
                                    fontWeight: 600
                                  }}
                                >
                                  Free Plan
                                </Button>
                              ) : (
                                <Button
                                  fullWidth
                                  variant="contained"
                                  onClick={() => handleUpgradeClick(plan)}
                                  disabled={processingAction === 'upgrade' || (loadingAnnualPricing && billingCycle === 'annual')}
                                  startIcon={processingAction === 'upgrade' ? <CircularProgress size={20} /> : <TrendingUpIcon />}
                                  sx={{ 
                                    py: 1.5,
                                    borderRadius: 2,
                                    fontWeight: 600,
                                    background: 'linear-gradient(45deg, #cf6f61 30%, #c79a4b 90%)',
                                    '&:hover': {
                                      background: 'linear-gradient(45deg, #cf6f61 60%, #c79a4b 100%)',
                                      transform: 'translateY(-2px)',
                                      boxShadow: '0 8px 25px rgba(254, 107, 139, 0.3)'
                                    },
                                    '&:disabled': {
                                      background: 'grey.300'
                                    },
                                    transition: 'all 0.3s ease'
                                  }}
                                >
                                  {processingAction === 'upgrade' ? 'Processing...' :
                                   user?.subscription_plan === 'free' ? 'Upgrade Now' : 'Switch Plan'}
                                </Button>
                              )}
                            </Box>
                          </Paper>
                        </Grow>
                      </Grid>
                    );
                  })}
                </Grid>
              </CardContent>
            </Card>
          </Fade>
        </Grid>
      </Grid>
      
      {/* Cancel Confirmation Dialog */}
      <Dialog 
        open={cancelDialogOpen} 
        onClose={() => setCancelDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <WarningIcon color="warning" sx={{ mr: 1 }} />
            Cancel Subscription?
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
            <Typography variant="body1" fontWeight={600} gutterBottom>
              Are you sure you want to cancel your subscription?
            </Typography>
            <Box component="ul" sx={{ mt: 1, pl: 2 }}>
              <Typography component="li" variant="body2" color="text.secondary">
                Your subscription will remain active until {user?.subscription_expires_at 
                  ? new Date(user.subscription_expires_at).toLocaleDateString()
                  : 'the end of your billing period'}
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                You'll lose access to premium features after that date
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                You can reactivate anytime before the period ends
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                No refunds will be provided for unused time
              </Typography>
            </Box>
          </Alert>
          
          <Typography variant="body2" color="text.secondary">
            If you're experiencing issues, please <strong>contact our support team</strong> first. 
            We're here to help and may be able to resolve your concerns without cancelling.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={() => setCancelDialogOpen(false)}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            Keep Subscription
          </Button>
          <Button 
            onClick={handleCancelConfirm}
            color="error"
            variant="contained"
            disabled={processingAction === 'cancel'}
            startIcon={processingAction === 'cancel' ? <CircularProgress size={20} /> : null}
            sx={{ borderRadius: 2 }}
          >
            {processingAction === 'cancel' ? 'Cancelling...' : 'Yes, Cancel'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SubscriptionPage;